import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, EmptyState, Field, Icon, Input, Select, TextArea } from "@agentsaas/ui";
import { Loading } from "../layout/Loading";
import { NotFound } from "./NotFound";
import { SUPPORTED_LOCALES, type Locale } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime } from "../app/format";
import { qk, useProjects, useTenants } from "../data/queries";
import { ShellSlot } from "../layout/shell";
import { AdminLink } from "../admin/AdminPages";
import { useTenantLinks } from "../layout/SubFrame";

export function TenantSwitcher({ tid }: { tid: string }) {
  const { t } = useT();
  const tenants = useTenants();
  const navigate = useNavigate();
  const items = tenants.data ?? [];
  if (items.length <= 1) return <span className="ellipsis">{items[0]?.name}</span>;
  return (
    <select className="ui-input shell-lang" aria-label={t("tenant.switch")} value={tid} onChange={(e) => navigate(`/t/${e.target.value}`)}>
      {items.map((x) => (
        <option key={x.id} value={x.id}>
          {x.name}
        </option>
      ))}
    </select>
  );
}

export function ProjectsPage() {
  const { tid = "" } = useParams();
  const { t, locale } = useT();
  const user = useSession().state.user;
  const projects = useProjects(tid);
  const [open, setOpen] = useState(false);
  const links = useTenantLinks(tid);
  return (
    <div className="page">
      <ShellSlot name="title">
        <TenantSwitcher tid={tid} />
      </ShellSlot>
      <div className="page__head">
        <h1 className="page__title">{t("project.title")}</h1>
        <div className="row">
          <Link className="ui-btn ui-btn--secondary ui-btn--md" to={`/t/${tid}/templates`}>
            {t("template.title")}
          </Link>
          <Link className="ui-btn ui-btn--secondary ui-btn--md" to={`/t/${tid}/credentials`}>
            {t("credential.title")}
          </Link>
          {links.map((l) => (
            <Link key={l.to} className="ui-btn ui-btn--secondary ui-btn--md" to={l.to} data-testid={l.testid}>
              <Icon name={l.icon} size={16} />
              {l.label}
            </Link>
          ))}
          <AdminLink />
          <Button variant="primary" icon="plus" onClick={() => setOpen(true)} data-testid="new-project">
            {t("project.new")}
          </Button>
        </div>
      </div>
      {projects.isError ? (
        <NotFound />
      ) : projects.isPending ? (
        <Loading />
      ) : projects.data?.length ? (
        <ul className="list card" style={{ padding: 0 }}>
          {projects.data.map((p) => (
            <li key={p.id}>
              <Link className="list-item" to={`/p/${p.id}`}>
                <span className="grow">
                  <strong>{p.name}</strong>
                  {p.description ? <div className="muted small ellipsis">{p.description}</div> : null}
                </span>
                <span className="muted small">{t("project.created_at", { time: formatDateTime(p.createdAt, locale, user?.timezone, { dateStyle: "medium" }) })}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="folder" title={t("project.empty")} />
      )}
      <NewProjectDialog tid={tid} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function NewProjectDialog({ tid, open, onClose }: { tid: string; open: boolean; onClose(): void }) {
  const { t, locale: uiLocale } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [locale, setLocale] = useState<Locale>(uiLocale);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const p = await api.call("projectCreate", { params: { tid }, body: { name, description, locale } });
      await qc.invalidateQueries({ queryKey: qk.projects(tid) });
      onClose();
      navigate(`/p/${p.id}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("project.new")}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} disabled={!name.trim()} data-testid="project-submit">
            {t("common.create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Field label={t("project.name")} error={error ?? undefined}>
          {(id) => <Input id={id} data-autofocus value={name} onChange={(e) => setName(e.target.value)} maxLength={64} data-testid="project-name" />}
        </Field>
        <Field label={t("project.description")}>{(id) => <TextArea id={id} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />}</Field>
        <Field label={t("project.locale")} hint={t("project.locale_hint")}>
          {(id, d) => (
            <Select id={id} aria-describedby={d} value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
              {SUPPORTED_LOCALES.map((l) => (
                <option key={l} value={l}>
                  {t(`locale.${l}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </form>
    </Dialog>
  );
}
